import clsx from "clsx";
import React, { useState } from "react";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import LazyModal from "web/react/components/lazy-modal/lazy-modal";
import { modalAnimationMs } from "web/react/emo/theme.css";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import * as styles from "./modal.css";

export interface ModalProps {
    children: JSX.Element;
    isOpen: boolean;
    onAfterOpen?: () => void;
    onClose: () => void;
    contentLabel?: string;
    className?: string;
    closeClassName?: string;
    overlayClassName?: string;
    closable?: boolean;
}

export function Modal({
    children,
    isOpen,
    onAfterOpen,
    onClose,
    contentLabel,
    className,
    closeClassName,
    overlayClassName,
    closable = true,
}: ModalProps): null | JSX.Element {
    const [animateIn, setAnimateIn] = useState(false);
    const [animateOut, setAnimateOut] = useState(false);

    let appElement: HTMLElement | undefined;
    if (document.body) {
        appElement = document.body.querySelector<HTMLDivElement>(".site-wrapper") || undefined;
    }

    useIsomorphicLayoutEffect(() => {
        if (!isOpen) {
            setAnimateIn(false);
            setAnimateOut(false);
        }
    }, [isOpen]);

    function closeModal(): void {
        setAnimateOut(true);
        // allow animations time to complete
        setTimeout(function () {
            onClose();
        }, modalAnimationMs);
    }

    return (
        <LazyModal
            appElement={appElement}
            className={clsx(styles.modal, className, {
                [styles.animateIn]: animateIn,
                [styles.animateOut]: animateOut,
            })}
            contentLabel={contentLabel}
            isOpen={isOpen}
            onAfterOpen={(): void => {
                onAfterOpen?.();
                setAnimateIn(true);
            }}
            onRequestClose={closeModal}
            overlayClassName={clsx(styles.overlay, overlayClassName, {
                [styles.overlayAnimateIn]: animateIn,
                [styles.overlayAnimateOut]: animateOut,
            })}
            shouldCloseOnOverlayClick={closable}
            shouldCloseOnEsc={closable}
        >
            {closable && (
                <CloseButton
                    onClick={closeModal}
                    className={clsx(styles.closeButton, closeClassName)}
                />
            )}
            {children}
        </LazyModal>
    );
}
